import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import { saveAs } from "file-saver";
import htmlDocx from "html-docx-js/dist/html-docx";
import ExcelJS from "exceljs";
import { useNavigate } from "react-router-dom";
import { ContextSession } from "../../services/ContextSession";

const API_PREVIEW =
  "https://bei360.injupemp.gob.hn:8444/api/SGOPER/constancia-preview/";
const MAIN_BLUE = "#19498c";

function getAny(obj, keys) {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) {
      return obj[key];
    }
  }
  return "";
}

function formatDateOnly(value) {
  if (!value) return "N/A";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("es-HN");
  const text = String(value);
  return text.includes("T") ? text.split("T")[0] : text;
}

function formatPeriodValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  const text = String(value).trim();
  return text.includes("T") ? text.split("T")[0] : text;
}

function formatNum(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return value ?? "0";
  return n.toLocaleString("es-HN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getConstanciaHtml(detail) {
  const persona = detail?.persona || {};
  const historial = Array.isArray(detail?.historial) ? detail.historial : [];
  const totales = detail?.Totales || {};
  const meta = detail?.meta || {};
  const config = detail?.configuracion || {};

  const encargado = config.Encargado || "ENCARGADO";
  const encargadoCargo = config.Encargado_Cargo || "JEFE UNIDAD DE OPERACIONES";
  const encargadoGenero = String(config.Encargado_Genero || "")
    .trim()
    .toUpperCase();
  const encabezadoEncargado =
    encargadoGenero === "F" ? "La suscrita Encargada" : "El suscrito Encargado";
  const logoRaw = config.LogoBase64 || "";
  const logoDataUrl = logoRaw
    ? logoRaw.startsWith("data:")
      ? logoRaw
      : `data:image/png;base64,${logoRaw}`
    : "";
  const nombre = persona.NOMBRE || "N/A";
  const dni = persona.DNI || "N/A";
  const cdrp = persona.CDRP || "N/A";
  const estatus = persona.ESTATUS || "N/A";
  const aniosServicio = meta.anios_servicio ?? "N/A";
  const observaciones = meta.observaciones || "Ninguna";
  const preparadoPor = meta.NombreUsuario || meta.USUARIO || "N/A";

  const fechaRegistroMeta = getAny(meta, ["FechaRegistro", "fecha_registro"]);
  const fechaBase = fechaRegistroMeta
    ? new Date(fechaRegistroMeta)
    : new Date();
  const safeFecha = Number.isNaN(fechaBase.getTime()) ? new Date() : fechaBase;
  const fechaLarga = safeFecha.toLocaleDateString("es-HN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const rowsHtml = historial
    .map((item) => {
      const ramo = getAny(item, ["ramo", "RAMO"]);
      const periodoInicio = getAny(item, [
        "periodo_inicio",
        "periodo_inicial",
        "PERIODO_INICIAL",
      ]);
      const periodoFinal = getAny(item, ["periodo_final", "PERIODO_FINAL"]);
      const meses = getAny(item, ["meses", "MESES"]);
      const sueldo = getAny(item, ["sueldo", "SUELDO"]);
      const aportacion = getAny(item, [
        "cotizacion_individual",
        "COTIZACION_INDIVIDUAL",
        "cotizacionIndividual",
        "CotizacionIndividual",
        "cot_individual",
        "COT_INDIVIDUAL",
        "aportacion",
        "aporte",
        "APORTACION",
      ]);
      const totalAportacion = getAny(item, [
        "total_cotizacion",
        "TOTAL_COTIZACION",
        "totalCotizacion",
        "TotalCotizacion",
        "total_aportacion",
        "TOTAL_APORTACION",
      ]);
      const planilla = getAny(item, ["planilla", "PLANILLA"]);
      return `
        <tr>
          <td>${ramo ?? ""}</td>
          <td>${formatPeriodValue(periodoInicio)}</td>
          <td>${formatPeriodValue(periodoFinal)}</td>
          <td style="text-align:right;">${meses ?? ""}</td>
          <td style="text-align:right;">${formatNum(sueldo)}</td>
          <td style="text-align:right;">${formatNum(aportacion)}</td>
          <td style="text-align:right;">${formatNum(totalAportacion)}</td>
          <td>${planilla ?? ""}</td>
        </tr>
      `;
    })
    .join("");

  return `
  <!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Constancia Estado De Cuenta</title>
      <style>
        body { font-family: Arial, sans-serif; color: #000; margin: 24px; }
        .header { text-align: center; margin-bottom: 12px; }
        .logo-wrap { width: 100%; height: 70px; text-align: center; margin-bottom: 8px; overflow: hidden; }
        .logo {
          width: 180px !important;
          height: 60px !important;
          max-width: 180px !important;
          max-height: 60px !important;
          object-fit: contain;
          display: inline-block;
          border: 0;
        }
        .title { font-size: 34px; font-weight: 700; margin: 10px 0 20px; letter-spacing: 1px; }
        .text { font-size: 15px; line-height: 1.5; text-align: justify; }
        .status { margin: 22px 0 12px; font-size: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th, td { border: 1px solid #999; padding: 4px 6px; }
        th { background: #e6e6e6; font-weight: 700; }
        .totals-row td { background: #d3d3d3; font-weight: 700; }
        .meta { margin-top: 20px; font-size: 15px; line-height: 1.6; }
        .final { margin-top: 28px; font-size: 16px; }
        .firma-wrap { margin-top: 80px; text-align: center; }
        .firma-name { font-size: 20px; font-weight: 700; text-transform: uppercase; }
        .firma-cargo { font-size: 15px; margin-top: 6px; text-transform: uppercase; }
      </style>
    </head>
    <body>
      <div class="header">
        ${
          logoDataUrl
            ? `<div class="logo-wrap"><img class="logo" src="${logoDataUrl}" alt="Logo" width="180" height="60" style="width:180px;height:60px;max-width:180px;max-height:60px;display:inline-block;object-fit:contain;border:0;" /></div>`
            : ""
        }
        <div class="title">CONSTANCIA</div>
      </div>

      <div class="text">
        ${encabezadoEncargado} de la Unidad de Operaciones del INSTITUTO NACIONAL DE JUBILACIONES Y
        PENSIONES DE LOS EMPLEADOS Y FUNCIONARIOS DEL PODER EJECUTIVO (INJUPEMP), por este medio
        hace constar que el Sr.(a) <b>${nombre}</b>, con numero de identidad <b>${dni}</b> y
        Cotizante No. <b>${cdrp}</b>, tiene registradas cotizaciones en este sistema previsional
        las que a continuacion se detallan:
      </div>

      <div class="status">Estatus <b>${estatus}</b></div>

      <table>
        <thead>
          <tr>
            <th>Ramo</th>
            <th>Periodo inicial</th>
            <th>Periodo final</th>
            <th>Meses</th>
            <th>Sueldo</th>
            <th>Aportacion</th>
            <th>Total aportacion</th>
            <th>Planilla</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
          <tr class="totals-row">
            <td colspan="3" style="text-align:right;">TOTALES :</td>
            <td style="text-align:right;">${getAny(totales, ["TOTAL_MESES"]) || 0}</td>
            <td style="text-align:right;">${formatNum(getAny(totales, ["TOTAL_SUELDOS"]))}</td>
            <td style="text-align:right;">${formatNum(
              getAny(totales, ["TOTAL_COTIZACION_INDIVIDUAL"]),
            )}</td>
            <td style="text-align:right;">${formatNum(
              getAny(totales, ["TOTAL_TOTAL_COTIZACION"]),
            )}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div class="meta">
        Años de Servicio: <b>${aniosServicio}</b><br />
        Observaciones: <b>${observaciones}</b><br /><br />
        Preparado por: <b>${preparadoPor}</b>
      </div>

      <div class="final">
        Y para los fines que el(la) interesado(a) convenga, se le extiende la presente el día ${fechaLarga}.
      </div>

      <div class="firma-wrap">
        <div class="firma-name">${encargado}</div>
        <div class="firma-cargo">${encargadoCargo}</div>
      </div>
    </body>
  </html>
  `;
}

export default function EstadoDeCuenta() {
  const navigate = useNavigate();
  const sessi = useContext(ContextSession);
  const token = sessi?.sessionData?.token || "";

  const [cdrp, setCdrp] = useState("");
  const [aniosServicio, setAniosServicio] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tokenExpiredOpen, setTokenExpiredOpen] = useState(false);

  const [constanciaOpen, setConstanciaOpen] = useState(false);
  const [constanciaLoading, setConstanciaLoading] = useState(false);
  const [constanciaError, setConstanciaError] = useState("");
  const [constanciaHtml, setConstanciaHtml] = useState("");
  const [constanciaData, setConstanciaData] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const isTokenInvalidResponse = (result) => {
    if (!result || typeof result !== "object") return false;
    if (result.code === "token_not_valid") return true;
    if (
      typeof result.detail === "string" &&
      result.detail.toLowerCase().includes("token")
    ) {
      return true;
    }
    return false;
  };

  const handleTokenExpiredConfirm = () => {
    setTokenExpiredOpen(false);
    sessi.clearSession();
    navigate("/");
  };

  const handleCdrpChange = (event) => {
    const onlyDigits = event.target.value.replace(/\D/g, "");
    setCdrp(onlyDigits.slice(0, 9));
  };

  const handleGenerarConstancia = async () => {
    if (!cdrp.trim()) {
      setError("El CDRP es obligatorio.");
      return;
    }
    setError("");
    setLoading(true);
    setConstanciaOpen(true);
    setConstanciaLoading(true);
    setConstanciaError("");
    setConstanciaHtml("");
    setConstanciaData(null);

    try {
      const body = new FormData();
      body.append("cdrp", cdrp.trim());
      if (aniosServicio.trim())
        body.append("anios_servicio", aniosServicio.trim());
      if (observaciones.trim())
        body.append("observaciones", observaciones.trim());

      const response = await fetch(API_PREVIEW, {
        method: "POST",
        headers: authHeaders,
        body,
      });
      const result = await response.json().catch(() => null);
      if (response.status === 401 && isTokenInvalidResponse(result)) {
        setTokenExpiredOpen(true);
        setConstanciaOpen(false);
        return;
      }
      if (!response.ok) {
        const backendMsg =
          result?.detail ||
          result?.message ||
          `No se pudo generar la constancia (${response.status}).`;
        throw new Error(backendMsg);
      }

      setConstanciaData(result);
      setConstanciaHtml(getConstanciaHtml(result));
    } catch (err) {
      setConstanciaData(null);
      setConstanciaError(err.message || "Error al generar constancia.");
    } finally {
      setLoading(false);
      setConstanciaLoading(false);
    }
  };

  const handlePrintConstancia = () => {
    if (!constanciaHtml) return;
    const printHtml = constanciaHtml
      .replace(
        ".logo-wrap { width: 100%; height: 70px; text-align: center; margin-bottom: 8px; overflow: hidden; }",
        ".logo-wrap { width: 100%; height: 120px; text-align: center; margin-bottom: 8px; overflow: hidden; }",
      )
      .replace("width: 180px !important;", "width: 320px !important;")
      .replace("height: 60px !important;", "height: 105px !important;")
      .replace("max-width: 180px !important;", "max-width: 320px !important;")
      .replace("max-height: 60px !important;", "max-height: 105px !important;")
      .replace('width="180" height="60"', 'width="320" height="105"')
      .replace(
        "width:180px;height:60px;max-width:180px;max-height:60px;display:inline-block;object-fit:contain;border:0;",
        "width:320px;height:105px;max-width:320px;max-height:105px;display:inline-block;object-fit:contain;border:0;",
      );

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
    try {
      printWindow.document.title = "Constancia Estado De Cuenta";
    } catch (e) {}
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleDownloadDocx = () => {
    if (!constanciaHtml) return;
    const docxHtml = constanciaHtml
      .replace(
        ".logo-wrap { width: 100%; height: 70px; text-align: center; margin-bottom: 8px; overflow: hidden; }",
        ".logo-wrap { width: 100%; height: 105px; text-align: center; margin-bottom: 8px; overflow: hidden; }",
      )
      .replace("width: 180px !important;", "width: 300px !important;")
      .replace("height: 60px !important;", "height: 96px !important;")
      .replace("max-width: 180px !important;", "max-width: 300px !important;")
      .replace("max-height: 60px !important;", "max-height: 96px !important;")
      .replace('width="180" height="60"', 'width="300" height="96"')
      .replace(
        "width:180px;height:60px;max-width:180px;max-height:60px;display:inline-block;object-fit:contain;border:0;",
        "width:300px;height:96px;max-width:300px;max-height:96px;display:inline-block;object-fit:contain;border:0;",
      );
    const blob = htmlDocx.asBlob(docxHtml);
    saveAs(blob, "constancia_estado_cuenta.docx");
  };

  const handleDownloadExcel = async () => {
    if (!constanciaData || excelLoading) return;
    setExcelLoading(true);
    try {
      const persona = constanciaData?.persona || {};
      const historial = Array.isArray(constanciaData?.historial)
        ? constanciaData.historial
        : [];
      const totales = constanciaData?.Totales || {};
      const meta = constanciaData?.meta || {};
      const config = constanciaData?.configuracion || {};

      const nombre = persona.NOMBRE || "N/A";
      const dni = persona.DNI || "N/A";
      const cdrpPersona = persona.CDRP || cdrp || "N/A";
      const estatus = persona.ESTATUS || "N/A";
      const aniosServicioMeta = meta.anios_servicio ?? "N/A";
      const observacionesMeta = meta.observaciones || "Ninguna";
      const preparadoPor = meta.NombreUsuario || meta.USUARIO || "N/A";
      const encargado = config.Encargado || "ENCARGADO";
      const encargadoCargo =
        config.Encargado_Cargo || "JEFE UNIDAD DE OPERACIONES";
      const encargadoGenero = String(config.Encargado_Genero || "")
        .trim()
        .toUpperCase();
      const encabezadoEncargado =
        encargadoGenero === "F" ? "La suscrita Encargada" : "El suscrito Encargado";

      const fechaRegistroMeta = getAny(meta, ["FechaRegistro", "fecha_registro"]);
      const fechaBase = fechaRegistroMeta ? new Date(fechaRegistroMeta) : new Date();
      const safeFecha = Number.isNaN(fechaBase.getTime()) ? new Date() : fechaBase;
      const fechaLarga = safeFecha.toLocaleDateString("es-HN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      const toNumberOrValue = (value) => {
        if (value === null || value === undefined || value === "") return "";
        const n = Number(value);
        return Number.isNaN(n) ? value : n;
      };

      const parseLogo = (rawLogo) => {
        if (!rawLogo) return null;
        const text = String(rawLogo).trim();
        if (!text) return null;
        if (text.startsWith("data:")) {
          const match = text.match(/^data:image\/(png|jpeg|jpg);base64,(.+)$/i);
          if (!match) return null;
          const ext = match[1].toLowerCase() === "jpg" ? "jpeg" : match[1].toLowerCase();
          return { extension: ext, base64: match[2] };
        }
        return { extension: "png", base64: text };
      };

      const setRowBorder = (worksheetRef, rowIndex, fromCol, toCol, borderColor) => {
        const border = {
          top: { style: "thin", color: { argb: borderColor } },
          left: { style: "thin", color: { argb: borderColor } },
          bottom: { style: "thin", color: { argb: borderColor } },
          right: { style: "thin", color: { argb: borderColor } },
        };
        for (let col = fromCol; col <= toCol; col += 1) {
          worksheetRef.getCell(rowIndex, col).border = border;
        }
      };

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Constancia");
      workbook.creator = "INJUPEMP";
      workbook.lastModifiedBy = "INJUPEMP";
      workbook.created = new Date();
      workbook.modified = new Date();

      worksheet.views = [{ showGridLines: false }];
      worksheet.pageSetup = {
        orientation: "portrait",
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: { left: 0.5, right: 0.5, top: 0.45, bottom: 0.45, header: 0.2, footer: 0.2 },
      };

      const columnWidths = [8, 13, 13, 8, 12, 12, 12, 14];
      columnWidths.forEach((width, idx) => {
        worksheet.getColumn(idx + 1).width = width;
      });

      let currentRow = 1;

      const logo = parseLogo(config.LogoBase64 || "");
      if (logo) {
        try {
          const imageId = workbook.addImage({
            base64: logo.base64,
            extension: logo.extension,
          });
          worksheet.addImage(imageId, {
            tl: { col: 2.15, row: 0.15 },
            ext: { width: 235, height: 58 },
          });
          currentRow = 5;
        } catch (e) {
          currentRow = 3;
        }
      } else {
        currentRow = 3;
      }

      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = "CONSTANCIA";
      titleCell.font = { name: "Arial", bold: true, size: 18 };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getRow(currentRow).height = 28;
      currentRow += 2;

      const textFont = { name: "Arial", size: 10 };
      const boldTextFont = { name: "Arial", size: 10, bold: true };

      const bodyText = {
        richText: [
          {
            text:
              `${encabezadoEncargado} de la Unidad de Operaciones del INSTITUTO NACIONAL DE JUBILACIONES Y ` +
              "PENSIONES DE LOS EMPLEADOS Y FUNCIONARIOS DEL PODER EJECUTIVO (INJUPEMP), " +
              "por este medio hace constar que el Sr.(a) ",
            font: textFont,
          },
          { text: String(nombre), font: boldTextFont },
          { text: ", con numero de identidad ", font: textFont },
          { text: String(dni), font: boldTextFont },
          { text: " y Cotizante No. ", font: textFont },
          { text: String(cdrpPersona), font: boldTextFont },
          {
            text:
              ", tiene registradas cotizaciones en este sistema previsional " +
              "las que a continuacion se detallan:",
            font: textFont,
          },
        ],
      };

      const bodyStartRow = currentRow;
      const bodyEndRow = currentRow + 2;
      worksheet.mergeCells(`A${bodyStartRow}:H${bodyEndRow}`);
      const bodyCell = worksheet.getCell(`A${bodyStartRow}`);
      bodyCell.value = bodyText;
      bodyCell.alignment = {
        horizontal: "left",
        vertical: "top",
        wrapText: true,
      };
      for (let r = bodyStartRow; r <= bodyEndRow; r += 1) {
        worksheet.getRow(r).height = 17;
      }
      currentRow = bodyEndRow + 2;

      worksheet.getCell(`A${currentRow}`).value = "Estatus";
      worksheet.getCell(`A${currentRow}`).font = { name: "Arial", size: 10, bold: true };
      worksheet.getCell(`B${currentRow}`).value = String(estatus).toUpperCase();
      worksheet.getCell(`B${currentRow}`).font = { name: "Arial", size: 10, bold: true };
      currentRow += 2;

      const headerRowIndex = currentRow;
      const headers = [
        "Ramo",
        "Periodo inicial",
        "Periodo final",
        "Meses",
        "Sueldo",
        "Aportacion",
        "Total aportacion",
        "Planilla",
      ];

      headers.forEach((header, idx) => {
        const cell = worksheet.getCell(headerRowIndex, idx + 1);
        cell.value = header;
        cell.font = { name: "Arial", bold: true, size: 9 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });
      worksheet.getRow(headerRowIndex).height = 27;
      setRowBorder(worksheet, headerRowIndex, 1, 8, "FF7F7F7F");
      currentRow += 1;

      historial.forEach((item, idx) => {
        const ramo = getAny(item, ["ramo", "RAMO"]);
        const periodoInicio = getAny(item, [
          "periodo_inicio",
          "periodo_inicial",
          "PERIODO_INICIAL",
        ]);
        const periodoFinal = getAny(item, ["periodo_final", "PERIODO_FINAL"]);
        const meses = getAny(item, ["meses", "MESES"]);
        const sueldo = getAny(item, ["sueldo", "SUELDO"]);
        const aportacion = getAny(item, [
          "cotizacion_individual",
          "COTIZACION_INDIVIDUAL",
          "cotizacionIndividual",
          "CotizacionIndividual",
          "cot_individual",
          "COT_INDIVIDUAL",
          "aportacion",
          "aporte",
          "APORTACION",
        ]);
        const totalAportacion = getAny(item, [
          "total_cotizacion",
          "TOTAL_COTIZACION",
          "totalCotizacion",
          "TotalCotizacion",
          "total_aportacion",
          "TOTAL_APORTACION",
        ]);
        const planilla = getAny(item, ["planilla", "PLANILLA"]);

        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = ramo ?? "";
        row.getCell(2).value = formatPeriodValue(periodoInicio);
        row.getCell(3).value = formatPeriodValue(periodoFinal);
        row.getCell(4).value = toNumberOrValue(meses);
        row.getCell(5).value = toNumberOrValue(sueldo);
        row.getCell(6).value = toNumberOrValue(aportacion);
        row.getCell(7).value = toNumberOrValue(totalAportacion);
        row.getCell(8).value = planilla ?? "";

        row.font = { name: "Arial", size: 9 };
        row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
        row.getCell(2).alignment = { horizontal: "center", vertical: "middle" };
        row.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
        row.getCell(8).alignment = { horizontal: "left", vertical: "middle" };

        [5, 6, 7].forEach((col) => {
          if (typeof row.getCell(col).value === "number") {
            row.getCell(col).numFmt = "#,##0.00";
          }
        });
        if (typeof row.getCell(4).value === "number") {
          row.getCell(4).numFmt = "0";
        }
        [4, 5, 6, 7].forEach((col) => {
          row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
        });
        if (idx % 2 !== 0) {
          for (let col = 1; col <= 8; col += 1) {
            row.getCell(col).fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8F8F8" },
            };
          }
        }
        setRowBorder(worksheet, currentRow, 1, 8, "FFB0B0B0");

        currentRow += 1;
      });

      const totalRowIndex = currentRow;
      worksheet.mergeCells(`A${totalRowIndex}:C${totalRowIndex}`);
      worksheet.getCell(`A${totalRowIndex}`).value = "TOTALES :";
      worksheet.getCell(`A${totalRowIndex}`).font = { name: "Arial", size: 9, bold: true };
      worksheet.getCell(`A${totalRowIndex}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
      worksheet.getCell(`D${totalRowIndex}`).value =
        toNumberOrValue(getAny(totales, ["TOTAL_MESES"])) || 0;
      worksheet.getCell(`E${totalRowIndex}`).value = toNumberOrValue(
        getAny(totales, ["TOTAL_SUELDOS"]),
      );
      worksheet.getCell(`F${totalRowIndex}`).value = toNumberOrValue(
        getAny(totales, ["TOTAL_COTIZACION_INDIVIDUAL"]),
      );
      worksheet.getCell(`G${totalRowIndex}`).value = toNumberOrValue(
        getAny(totales, ["TOTAL_TOTAL_COTIZACION"]),
      );

      [5, 6, 7].forEach((col) => {
        if (typeof worksheet.getCell(totalRowIndex, col).value === "number") {
          worksheet.getCell(totalRowIndex, col).numFmt = "#,##0.00";
        }
      });
      if (typeof worksheet.getCell(totalRowIndex, 4).value === "number") {
        worksheet.getCell(totalRowIndex, 4).numFmt = "0";
      }

      for (let col = 1; col <= 8; col += 1) {
        const cell = worksheet.getCell(totalRowIndex, col);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE6E6E6" },
        };
        cell.font = { bold: true };
        if (col >= 4 && col <= 7) {
          cell.alignment = { horizontal: "right", vertical: "middle" };
        }
      }
      setRowBorder(worksheet, totalRowIndex, 1, 8, "FF7F7F7F");

      currentRow = totalRowIndex + 2;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = {
        richText: [
          { text: "Años de Servicio: ", font: textFont },
          { text: String(aniosServicioMeta), font: boldTextFont },
        ],
      };
      worksheet.getCell(`A${currentRow}`).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      currentRow += 1;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = {
        richText: [
          { text: "Observaciones: ", font: textFont },
          { text: String(observacionesMeta), font: boldTextFont },
        ],
      };
      worksheet.getCell(`A${currentRow}`).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      currentRow += 1;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getRow(currentRow).height = 8;

      currentRow += 1;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = {
        richText: [
          { text: "Preparado por: ", font: textFont },
          { text: String(preparadoPor), font: boldTextFont },
        ],
      };
      worksheet.getCell(`A${currentRow}`).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      currentRow += 2;
      worksheet.mergeCells(`A${currentRow}:H${currentRow + 1}`);
      worksheet.getCell(`A${currentRow}`).value =
        `Y para los fines que el(la) interesado(a) convenga, se le extiende la presente el día ${fechaLarga}.`;
      worksheet.getCell(`A${currentRow}`).alignment = {
        wrapText: true,
        horizontal: "left",
        vertical: "top",
      };
      worksheet.getCell(`A${currentRow}`).font = { name: "Arial", size: 10 };
      worksheet.getRow(currentRow).height = 18;
      worksheet.getRow(currentRow + 1).height = 18;

      currentRow += 4;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value =
        "_____________________________________________";
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
      worksheet.getCell(`A${currentRow}`).font = { name: "Arial", size: 10 };

      currentRow += 1;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = encargado;
      worksheet.getCell(`A${currentRow}`).font = {
        name: "Arial",
        size: 11,
        bold: true,
      };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };

      currentRow += 1;
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = encargadoCargo;
      worksheet.getCell(`A${currentRow}`).font = { name: "Arial", size: 10 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };

      const fechaArchivo = safeFecha.toISOString().split("T")[0];
      const cdrpFile = String(cdrpPersona).replace(/[^0-9A-Za-z_-]/g, "") || "sin_cdrp";
      const nombreArchivo = `constancia_estado_cuenta_${cdrpFile}_${fechaArchivo}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, nombreArchivo);
    } catch (err) {
      setError(err?.message || "Error al descargar Excel.");
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        ESTADO DE CUENTA
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Datos para generar constancia
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="CDRP"
                value={cdrp}
                onChange={handleCdrpChange}
                fullWidth
                required
                inputProps={{
                  maxLength: 9,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                helperText="Obligatorio. Maximo 9 digitos."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Años de servicio"
                value={aniosServicio}
                onChange={(e) => setAniosServicio(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleGenerarConstancia}
              disabled={loading}
              sx={{
                backgroundColor: MAIN_BLUE,
                "&:hover": { backgroundColor: MAIN_BLUE },
              }}
            >
              {loading ? "Generando..." : "Generar constancia"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={constanciaOpen}
        onClose={() => setConstanciaOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Vista previa de constancia</DialogTitle>
        <DialogContent dividers>
          {constanciaLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : constanciaError ? (
            <Alert severity="error">{constanciaError}</Alert>
          ) : (
            <Box
              sx={{
                width: "100%",
                minHeight: "70vh",
                border: "1px solid #ddd",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <iframe
                title="constancia-preview"
                srcDoc={constanciaHtml}
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConstanciaOpen(false)}
            sx={{ color: MAIN_BLUE }}
          >
            Cerrar
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintConstancia}
            sx={{ color: MAIN_BLUE, borderColor: MAIN_BLUE }}
            disabled={!constanciaHtml || constanciaLoading}
          >
            Imprimir
          </Button>
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={handleDownloadDocx}
            sx={{
              backgroundColor: MAIN_BLUE,
              "&:hover": { backgroundColor: MAIN_BLUE },
            }}
            disabled={!constanciaHtml || constanciaLoading}
          >
            Descargar DOCX
          </Button>
          <Button
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={handleDownloadExcel}
            sx={{
              backgroundColor: MAIN_BLUE,
              "&:hover": { backgroundColor: MAIN_BLUE },
            }}
            disabled={!constanciaData || constanciaLoading || excelLoading}
          >
            {excelLoading ? "Generando Excel..." : "Descargar Excel"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tokenExpiredOpen}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sesion vencida</DialogTitle>
        <DialogContent>
          <Typography>
            El token vencio. Debe volver a ingresar al sistema.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleTokenExpiredConfirm}
            sx={{
              backgroundColor: MAIN_BLUE,
              "&:hover": { backgroundColor: MAIN_BLUE },
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
